import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Table,
  Input,
  Button,
  Card,
  Modal,
  Select,
  Switch,
  Tag,
  Space,
  Tooltip,
  Popconfirm,
  Progress,
  DatePicker,
  Row,
  Col,
  Divider,
  Badge,
} from "antd";
import {
  CopyOutlined,
  EditOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { apiRequests } from "src/shared/api/api";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import locale from "antd/es/date-picker/locale/ru_RU";

dayjs.locale("ru");

const { Option } = Select;
const { Search } = Input;

const TablePromocode = ({ token }) => {
  const [promocodes, setPromocodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [search, setSearch] = useState("");
  const [isActiveFilter, setIsActiveFilter] = useState("all");
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
  });

  // Модальные окна
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [runOpen, setRunOpen] = useState(false);
  const [selectedPromocode, setSelectedPromocode] = useState(null);

  // Формы
  const [createForm, setCreateForm] = useState({
    code: "",
    points_amount: 100,
    type: "permanent",
    max_usages: 0,
    valid_after: null,
    valid_until: null,
    is_active: true,
    organization_id: null,
    distributor_id: null,
  });

  const [editForm, setEditForm] = useState({
    points_amount: 0,
    max_usages: 0,
    valid_after: null,
    valid_until: null,
    is_active: true,
  });

  const [runForm, setRunForm] = useState({
    code: "",
    phone_number: "",
  });

  // Загрузка организаций
  const loadOrganizations = useCallback(async () => {
    setLoadingOrgs(true);
    try {
      const response = await apiRequests.organizations.getItems({
        token,
        limit: 100,
      });
      setOrganizations(response.data.result || []);
    } catch (error) {
      toast.error("Не удалось загрузить организации");
    } finally {
      setLoadingOrgs(false);
    }
  }, [token]);

  // Загрузка промокодов
  const loadPromocodes = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        token,
        limit: pagination.pageSize,
        offset: (pagination.page - 1) * pagination.pageSize,
        ...(search && { search }),
        ...(isActiveFilter !== "all" && {
          is_active: isActiveFilter === "active",
        }),
        sort: "created_at:desc",
      };

      const response = await apiRequests.promocodes.getItems(params);
      setPromocodes(response.data);
      setPagination((prev) => ({ ...prev, total: response.data.length }));
    } catch (error) {
      toast.error("Не удалось загрузить промокоды");
    } finally {
      setLoading(false);
    }
  }, [token, pagination.page, pagination.pageSize, search, isActiveFilter]);

  // Создание промокода
  const handleCreate = async () => {
    try {
      // Проверка обязательных полей
      if (!createForm.code.trim()) {
        toast.error("Введите код промокода");
        return;
      }

      if (!createForm.organization_id) {
        toast.error("Выберите организацию");
        return;
      }

      const payload = {
        code: createForm.code,
        points_amount: createForm.points_amount,
        type: createForm.type,
        is_active: createForm.is_active,
        organization_id: createForm.organization_id,
      };

      if (createForm.max_usages !== null && createForm.max_usages !== 0) {
        payload.max_usages = createForm.max_usages;
      }
      if (
        createForm.distributor_id !== null &&
        createForm.distributor_id !== 0
      ) {
        payload.distributor_id = createForm.distributor_id;
      }
      if (createForm.valid_after) {
        payload.valid_after = dayjs(createForm.valid_after).unix();
      }
      if (createForm.valid_until) {
        payload.valid_until = dayjs(createForm.valid_until).unix();
      }

      const response = await apiRequests.promocodes.create(payload);

      toast.success("Промокод успешно создан!", {
        description: `Код: ${response.data.code}`,
      });

      setCreateOpen(false);
      setCreateForm({
        code: "",
        points_amount: 100,
        type: "permanent",
        max_usages: 0,
        valid_after: null,
        valid_until: null,
        is_active: true,
        organization_id: null,
        distributor_id: null,
      });

      loadPromocodes();
    } catch (error) {
      const errorMsg =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Не удалось создать промокод";

      if (typeof errorMsg === "string") {
        toast.error("Ошибка создания промокода", {
          description: errorMsg,
        });
      } else if (Array.isArray(errorMsg)) {
        const errors = errorMsg
          .map((err) => err.msg || JSON.stringify(err))
          .join(", ");
        toast.error("Ошибка создания промокода", {
          description: errors,
        });
      } else {
        toast.error("Ошибка создания промокода");
      }
    }
  };

  // Редактирование промокода
  const handleEdit = async () => {
    if (!selectedPromocode) return;
    const payload = {};
    if (editForm.points_amount !== undefined && editForm.points_amount !== 0) {
      payload.points_amount = editForm.points_amount;
    }
    if (editForm.max_usages !== null && editForm.max_usages !== 0) {
      payload.max_usages = editForm.max_usages;
    }
    if (editForm.valid_after) {
      payload.valid_after = dayjs(editForm.valid_after).unix();
    }
    if (editForm.valid_until) {
      payload.valid_until = dayjs(editForm.valid_until).unix();
    }
    if (editForm.is_active !== undefined) {
      payload.is_active = editForm.is_active;
    }

    try {
      await apiRequests.promocodes.update(selectedPromocode.id, payload);

      toast.success("Промокод успешно обновлен");

      setEditOpen(false);
      setSelectedPromocode(null);
      loadPromocodes();
    } catch (error) {
      const errorMsg =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Не удалось обновить промокод";
      toast.error("Ошибка обновления промокода", {
        description: errorMsg,
      });
    }
  };

  // Активация промокода
  const handleRun = async () => {
    try {
      if (!runForm.code.trim()) {
        toast.error("Введите код промокода");
        return;
      }

      if (!runForm.phone_number.trim()) {
        toast.error("Введите номер телефона");
        return;
      }

      const response = await apiRequests.promocodes.run(runForm);

      toast.success("Промокод активирован!", {
        description: (
          <div style={{ marginTop: "8px" }}>
            <div style={{ fontWeight: "500" }}>{response.data.message}</div>
            <div style={{ marginTop: "8px", fontSize: "14px" }}>
              <div>
                Добавлено баллов:{" "}
                <span style={{ fontWeight: "600" }}>
                  {response.data.added_points}
                </span>
              </div>
              <div>
                Новый баланс:{" "}
                <span style={{ fontWeight: "600" }}>
                  {response.data.new_balance}
                </span>
              </div>
              {response.data.transaction_id && (
                <div
                  style={{ color: "#666", fontSize: "12px", marginTop: "4px" }}
                >
                  ID транзакции: {response.data.transaction_id}
                </div>
              )}
            </div>
          </div>
        ),
      });

      setRunOpen(false);
      setRunForm({ code: "", phone_number: "" });
    } catch (error) {
      const errorMsg =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Не удалось активировать промокод";
      toast.error("Ошибка активации промокода", {
        description: errorMsg,
      });
    }
  };

  // Удаление промокода
  const handleDelete = async () => {
    if (!selectedPromocode) return;

    try {
      await apiRequests.promocodes.delete(selectedPromocode.id);

      toast.success("Промокод удален");

      setSelectedPromocode(null);
      loadPromocodes();
    } catch (error) {
      const errorMsg =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Не удалось удалить промокод";
      toast.error("Ошибка удаления промокода", {
        description: errorMsg,
      });
    }
  };

  // Копирование кода
  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Код скопирован в буфер обмена", {
      description: code,
    });
  };

  // Форматирование даты
  const formatDate = (timestamp) => {
    if (!timestamp || timestamp === 0) return "Бессрочно";
    return dayjs(timestamp * 1000).format("DD.MM.YYYY HH:mm");
  };

  useEffect(() => {
    if (token) {
      loadPromocodes();
      loadOrganizations();
    }
  }, [token, loadPromocodes, loadOrganizations]);

  // Открытие редактирования
  const openEditDialog = (promocode) => {
    setSelectedPromocode(promocode);
    setEditForm({
      points_amount: promocode.points_amount,
      max_usages: promocode.max_usages,
      valid_after: promocode.valid_after
        ? dayjs(promocode.valid_after * 1000)
        : null,
      valid_until: promocode.valid_until
        ? dayjs(promocode.valid_until * 1000)
        : null,
      is_active: promocode.is_active,
    });
    setEditOpen(true);
  };

  // Открытие активации с предзаполненным кодом
  const openRunDialog = (code = "") => {
    setRunForm({ code, phone_number: "" });
    setRunOpen(true);
  };

  // Колонки таблицы
  const columns = [
    {
      title: "Код",
      dataIndex: "code",
      key: "code",
      render: (code, record) => (
        <Space>
          <Tag color="blue" style={{ fontSize: "14px", fontWeight: "bold" }}>
            {code}
          </Tag>
          <Tooltip title="Копировать код">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => handleCopyCode(code)}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: "Баллы",
      dataIndex: "points_amount",
      key: "points_amount",
      render: (points) => (
        <span style={{ fontWeight: "bold", color: "#1890ff" }}>
          {points.toLocaleString()} баллов
        </span>
      ),
    },
    {
      title: "Организация",
      key: "organization",
      render: (_, record) => {
        const org = organizations.find((o) => o.id === record.organization_id);
        return org ? (
          <div>
            <div style={{ fontWeight: "500" }}>
              {org.short_name || org.name}
            </div>
            {org.inn && (
              <div style={{ color: "#8c8c8c", fontSize: "12px" }}>
                ИНН: {org.inn}
              </div>
            )}
          </div>
        ) : (
          <span style={{ color: "#8c8c8c" }}>—</span>
        );
      },
    },
    {
      title: "Использовано",
      key: "usage",
      render: (_, record) => {
        if (record.max_usages === 0) {
          return <span style={{ color: "#8c8c8c" }}>Безлимитно</span>;
        }
        const percent = (record.current_usages / record.max_usages) * 100;
        return (
          <div>
            <Progress percent={percent} size="small" />
            <div style={{ marginTop: "4px", fontSize: "12px" }}>
              {record.current_usages} / {record.max_usages} (осталось:{" "}
              {record.max_usages - record.current_usages})
            </div>
          </div>
        );
      },
    },
    {
      title: "Статус",
      dataIndex: "is_active",
      key: "status",
      render: (isActive) => (
        <Badge
          status={isActive ? "success" : "default"}
          text={isActive ? "Активен" : "Неактивен"}
        />
      ),
    },
    {
      title: "Действует с",
      key: "valid_after",
      render: (_, record) => (
        <div style={{ fontSize: "12px" }}>{formatDate(record.valid_after)}</div>
      ),
    },
    {
      title: "Действует до",
      key: "valid_until",
      render: (_, record) => (
        <div style={{ fontSize: "12px" }}>{formatDate(record.valid_until)}</div>
      ),
    },
    {
      title: "Создан",
      key: "created_at",
      render: (_, record) => (
        <div style={{ fontSize: "12px" }}>{formatDate(record.created_at)}</div>
      ),
    },
    {
      title: "Действия",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="Активировать промокод">
            <Button
              type="text"
              icon={<PlayCircleOutlined />}
              onClick={() => openRunDialog(record.code)}
              disabled={!record.is_active}
            />
          </Tooltip>
          <Tooltip title="Редактировать">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => openEditDialog(record)}
            />
          </Tooltip>
          <Tooltip title="Удалить">
            <Popconfirm
              title="Удаление промокода"
              description={
                <div>
                  <div>
                    Вы уверены, что хотите удалить промокод{" "}
                    <strong>"{record.code}"</strong>?
                  </div>
                  <Divider style={{ margin: "8px 0" }} />
                  <div style={{ color: "#ff4d4f" }}>
                    Это действие нельзя отменить. Промокод будет помечен как
                    удаленный и перестанет быть активным.
                  </div>
                </div>
              }
              onConfirm={() => {
                setSelectedPromocode(record);
                handleDelete();
              }}
              okText="Удалить"
              cancelText="Отмена"
              okButtonProps={{ danger: true }}
            >
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Хедер с кнопками и фильтрами */}
      <Card
        style={{
          marginBottom: "16px",
          backgroundColor: "#f5f5f5",
          borderColor: "#f5f5f5",
          padding: "0 !important",
        }}
        bodyStyle={{ padding: "0" }}
      >
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreateOpen(true)}
              >
                Создать промокод
              </Button>
              <Button
                icon={<PlayCircleOutlined />}
                onClick={() => openRunDialog()}
              >
                Активировать
              </Button>
            </Space>
          </Col>
          <Col>
            <Space>
              <Search
                placeholder="Поиск по коду..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ width: 200 }}
                prefix={<SearchOutlined />}
                allowClear
              />
              <Select
                value={isActiveFilter}
                onChange={setIsActiveFilter}
                style={{ width: 150 }}
                placeholder="Статус"
              >
                <Option value="all">Все</Option>
                <Option value="active">Активные</Option>
                <Option value="inactive">Неактивные</Option>
              </Select>
              <Tooltip title="Обновить">
                <Button icon={<ReloadOutlined />} onClick={loadPromocodes} />
              </Tooltip>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Таблица */}
      <Card>
        <Table
          columns={columns}
          dataSource={promocodes}
          rowKey="id"
          loading={loading}
          locale={{
            emptyText: (
              <div style={{ padding: "40px 0", textAlign: "center" }}>
                <FilterOutlined
                  style={{
                    fontSize: "32px",
                    color: "#bfbfbf",
                    marginBottom: "16px",
                  }}
                />
                <div>Промокоды не найдены</div>
                {search && (
                  <div style={{ color: "#8c8c8c", marginTop: "8px" }}>
                    Попробуйте изменить параметры поиска
                  </div>
                )}
              </div>
            ),
          }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onChange: (page, pageSize) => {
              setPagination({ ...pagination, page, pageSize });
            },
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} из ${total} промокодов`,
          }}
        />
      </Card>

      {/* Модальное окно создания промокода */}
      <Modal
        title="Создание промокода"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={handleCreate}
        width={800}
        okText="Создать"
        cancelText="Отмена"
        okButtonProps={{ disabled: loadingOrgs }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", marginBottom: "4px" }}>
                Код промокода *
              </label>
              <Input
                value={createForm.code}
                onChange={(e) =>
                  setCreateForm({
                    ...createForm,
                    code: e.target.value.toUpperCase(),
                  })
                }
                placeholder="SUMMER2024"
                style={{ textTransform: "uppercase" }}
              />
            </div>
          </Col>
          <Col span={12}>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", marginBottom: "4px" }}>
                Количество баллов *
              </label>
              <Input
                type="number"
                min="1"
                value={createForm.points_amount}
                onChange={(e) =>
                  setCreateForm({
                    ...createForm,
                    points_amount: parseInt(e.target.value) || 0,
                  })
                }
              />
            </div>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", marginBottom: "4px" }}>
                Организация *
              </label>
              <Select
                value={createForm.organization_id}
                onChange={(value) =>
                  setCreateForm({
                    ...createForm,
                    organization_id: value,
                  })
                }
                loading={loadingOrgs}
                placeholder={
                  loadingOrgs ? "Загрузка..." : "Выберите организацию"
                }
                style={{ width: "100%" }}
              >
                {organizations.map((org) => (
                  <Option key={org.id} value={org.id}>
                    {org.short_name || org.name}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>
          <Col span={12}>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", marginBottom: "4px" }}>
                Тип промокода
              </label>
              <Select
                value={createForm.type}
                onChange={(value) =>
                  setCreateForm({ ...createForm, type: value })
                }
                style={{ width: "100%" }}
              >
                <Option value="permanent">Постоянный</Option>
              </Select>
            </div>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", marginBottom: "4px" }}>
                Макс. использований
              </label>
              <Input
                type="number"
                min="0"
                value={createForm.max_usages}
                onChange={(e) =>
                  setCreateForm({
                    ...createForm,
                    max_usages: parseInt(e.target.value) || 0,
                  })
                }
              />
              <div
                style={{ fontSize: "12px", color: "#8c8c8c", marginTop: "4px" }}
              >
                0 - без ограничений
              </div>
            </div>
          </Col>
          <Col span={12}>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", marginBottom: "4px" }}>
                ID дистрибьютора (опционально)
              </label>
              <Input
                type="number"
                value={createForm.distributor_id}
                onChange={(e) =>
                  setCreateForm({
                    ...createForm,
                    distributor_id: parseInt(e.target.value) || 0,
                  })
                }
                placeholder="0"
              />
            </div>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", marginBottom: "4px" }}>
                Действует с
              </label>
              <DatePicker
                value={createForm.valid_after}
                onChange={(date) =>
                  setCreateForm({ ...createForm, valid_after: date })
                }
                locale={locale}
                style={{ width: "100%" }}
              />
            </div>
          </Col>
          <Col span={12}>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", marginBottom: "4px" }}>
                Действует до
              </label>
              <DatePicker
                value={createForm.valid_until}
                onChange={(date) =>
                  setCreateForm({ ...createForm, valid_until: date })
                }
                locale={locale}
                style={{ width: "100%" }}
              />
            </div>
          </Col>
        </Row>

        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            border: "1px solid #d9d9d9",
            borderRadius: "6px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Switch
            checked={createForm.is_active}
            onChange={(checked) =>
              setCreateForm({ ...createForm, is_active: checked })
            }
          />
          <div>
            <div style={{ fontWeight: "500" }}>Активен</div>
            <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
              {createForm.is_active
                ? "Промокод активен и может использоваться"
                : "Промокод неактивен и не может использоваться"}
            </div>
          </div>
        </div>
      </Modal>

      {/* Модальное окно редактирования промокода */}
      <Modal
        title="Редактирование промокода"
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={handleEdit}
        okText="Сохранить"
        cancelText="Отмена"
      >
        {selectedPromocode && (
          <div style={{ marginBottom: "16px" }}>
            <strong>Код:</strong> {selectedPromocode.code}
          </div>
        )}

        <div style={{ marginBottom: "16px" }}>
          <label style={{ display: "block", marginBottom: "4px" }}>
            Количество баллов
          </label>
          <Input
            type="number"
            min="1"
            value={editForm.points_amount}
            onChange={(e) =>
              setEditForm({
                ...editForm,
                points_amount: parseInt(e.target.value) || 0,
              })
            }
          />
        </div>

        <div style={{ marginBottom: "16px" }}>
          <label style={{ display: "block", marginBottom: "4px" }}>
            Макс. использований (0 = безлимит)
          </label>
          <Input
            type="number"
            min="0"
            value={editForm.max_usages}
            onChange={(e) =>
              setEditForm({
                ...editForm,
                max_usages: parseInt(e.target.value) || 0,
              })
            }
          />
        </div>

        <Row gutter={16}>
          <Col span={12}>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", marginBottom: "4px" }}>
                Действует с
              </label>
              <DatePicker
                value={editForm.valid_after}
                onChange={(date) =>
                  setEditForm({ ...editForm, valid_after: date })
                }
                locale={locale}
                style={{ width: "100%" }}
              />
            </div>
          </Col>
          <Col span={12}>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", marginBottom: "4px" }}>
                Действует до
              </label>
              <DatePicker
                value={editForm.valid_until}
                onChange={(date) =>
                  setEditForm({ ...editForm, valid_until: date })
                }
                locale={locale}
                style={{ width: "100%" }}
              />
            </div>
          </Col>
        </Row>

        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            border: "1px solid #d9d9d9",
            borderRadius: "6px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Switch
            checked={editForm.is_active}
            onChange={(checked) =>
              setEditForm({ ...editForm, is_active: checked })
            }
          />
          <div>
            <div style={{ fontWeight: "500" }}>Активен</div>
            <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
              {editForm.is_active
                ? "Промокод активен и может использоваться"
                : "Промокод неактивен и не может использоваться"}
            </div>
          </div>
        </div>
      </Modal>

      {/* Модальное окно активации промокода */}
      <Modal
        title="Активация промокода"
        open={runOpen}
        onCancel={() => setRunOpen(false)}
        onOk={handleRun}
        okText="Активировать"
        cancelText="Отмена"
      >
        <div style={{ marginBottom: "16px" }}>
          <label style={{ display: "block", marginBottom: "4px" }}>
            Код промокода
          </label>
          <Input
            value={runForm.code}
            onChange={(e) =>
              setRunForm({
                ...runForm,
                code: e.target.value.toUpperCase(),
              })
            }
            placeholder="SUMMER2024"
            style={{ textTransform: "uppercase" }}
          />
        </div>

        <div style={{ marginBottom: "16px" }}>
          <label style={{ display: "block", marginBottom: "4px" }}>
            Номер телефона
          </label>
          <Input
            value={runForm.phone_number}
            onChange={(e) =>
              setRunForm({ ...runForm, phone_number: e.target.value })
            }
            placeholder="+79991234567"
          />
        </div>
      </Modal>
    </div>
  );
};

export default TablePromocode;
